import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ArrowRight, Film } from "lucide-react";
import { getPublicProgram, type PublicFilm } from "@/lib/films.functions";
import { resolveBadgeStyle, pickPrimaryBadge } from "@/lib/badges";
import { useCustomBadges } from "@/hooks/use-custom-badges";
import { ActivitiesBadge } from "@/components/festival/FilmActivities";

const WEEKDAYS_SHORT_NL = ["zo", "ma", "di", "wo", "do", "vr", "za"];
const MONTHS_NL = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];
const MONTHS_SHORT_NL = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

function parseDate(iso: string): Date {
  // treat as local calendar date
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function formatChip(iso: string) {
  const d = parseDate(iso);
  return {
    weekday: WEEKDAYS_SHORT_NL[d.getDay()],
    day: d.getDate(),
    month: MONTHS_SHORT_NL[d.getMonth()],
  };
}

function formatLong(iso: string) {
  const d = parseDate(iso);
  return `${WEEKDAYS_SHORT_NL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()]}`;
}

export function FilmProgram() {
  const [payload, setPayload] = useState<{ sectionVisible: boolean; films: PublicFilm[] } | null>(null);
  const customBadges = useCustomBadges();

  useEffect(() => {
    getPublicProgram()
      .then(setPayload)
      .catch(() => setPayload({ sectionVisible: false, films: [] }));
  }, []);

  // Section is hidden globally
  if (!payload) return null;
  if (!payload.sectionVisible) return null;

  // Empty state: section is on, but no published films yet
  if (payload.films.length === 0) {
    return (
      <section id="programma" className="relative py-24 md:py-32">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
          <p className="font-mono-cinema text-[10px] text-gold">— Programma · editie 2026</p>
          <h2 className="mt-5 font-display text-4xl font-medium leading-[1.05] md:text-5xl">
            Negen avonden,
            <span className="italic text-gold"> negen films</span>.
          </h2>
          <p className="mt-6 text-foreground/70 md:text-lg">
            Het volledige programma wordt binnenkort onthuld. Meld je aan voor updates en wees er als eerste bij.
          </p>
          <div className="mt-8 flex justify-center">
            <Button asChild variant="hero">
              <a href="#updates">
                Ontvang het programma <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="programma" className="relative py-24 md:py-32">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="font-mono-cinema text-[10px] text-gold">— Programma · editie 2026</p>
          <h2 className="mt-5 font-display text-4xl font-medium leading-[1.05] md:text-5xl lg:text-6xl">
            Negen avonden,
            <span className="italic text-gold"> negen films</span>.
          </h2>
          <p className="mt-6 text-foreground/70 md:text-lg">
            Van klassieker tot verrassing, een overzicht van alle films die we deze zomer projecteren onder de sterren.
          </p>
        </div>

        {/* Poster grid */}
        <div className="mt-14 grid grid-cols-2 gap-5 sm:grid-cols-3 md:gap-7 lg:grid-cols-3 xl:gap-8">
          {payload.films.map((f) => {
            const chip = formatChip(f.play_date);
            const primaryBadge = pickPrimaryBadge(f.badges, customBadges);
            const primaryStyle = primaryBadge ? resolveBadgeStyle(primaryBadge, customBadges) : null;
            return (
              <Link key={f.id} to="/programma" hash={`film-${f.id}`} className="group relative flex flex-col">
                <div className="film-grain relative aspect-[2/3] w-full overflow-hidden rounded-xl border border-border bg-gradient-night transition-all duration-500 group-hover:-translate-y-1 group-hover:border-gold/60 group-hover:shadow-glow">
                  {f.poster_url ? (
                    <img
                      src={f.poster_url}
                      alt={f.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Film className="h-14 w-14 text-gold/40" />
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-night-deep/85 via-night-deep/10 to-transparent" />
                  <div className="pointer-events-none absolute inset-0 bg-projector opacity-30 mix-blend-overlay" />

                  {/* Date badge */}
                  <div className="absolute left-3 top-3 flex flex-col items-center rounded-md border border-gold/50 bg-night-deep/85 px-2.5 py-2 backdrop-blur-sm">
                    <span className="font-mono-cinema text-[10px] uppercase tracking-wider text-gold/90">
                      {chip.weekday}
                    </span>
                    <span className="font-display text-xl leading-none text-foreground md:text-2xl">{chip.day}</span>
                    <span className="font-mono-cinema text-[9px] uppercase tracking-wider text-foreground/60">
                      {chip.month}
                    </span>
                    {f.start_time && (
                      <span className="mt-1 border-t border-gold/30 pt-0.5 font-mono-cinema text-[9px] uppercase tracking-wider text-gold">
                        {f.start_time}
                      </span>
                    )}
                  </div>

                  {primaryBadge && primaryStyle && (
                    <span
                      className={`absolute right-3 top-3 rounded-full px-3 py-1.5 font-mono-cinema text-[10px] font-semibold uppercase tracking-wider backdrop-blur-sm ${primaryStyle.className}`}
                      style={primaryStyle.style}
                    >
                      {primaryBadge}
                    </span>
                  )}

                  {/* Title overlay */}
                  <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
                    {f.show_activities_badge && (
                      <div className="mb-2">
                        <ActivitiesBadge label={f.activities_badge_label} />
                      </div>
                    )}
                    <h3 className="font-display text-lg leading-tight text-foreground md:text-xl lg:text-[1.35rem]">
                      {f.title}
                    </h3>
                    {f.genre && (
                      <p className="mt-1.5 font-mono-cinema text-[10px] uppercase tracking-wider text-gold/90">
                        {f.genre}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col items-center gap-4">
          <Button asChild variant="hero" size="lg">
            <Link to="/programma">
              Bekijk het volledige programma <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <p className="font-mono-cinema text-[10px] text-foreground/60">
            ● Programma onder voorbehoud · Wijzigingen mogelijk
          </p>
        </div>
      </div>
    </section>
  );
}

export { formatLong };
